import { Injectable } from '@angular/core';
import { CNAME } from '@core/constants/cookies';
import { getCookie, setCookie } from '@core/utils';
import { v4 as uuidv4 } from 'uuid';

const base64urlFromBuffer = (buf: ArrayBuffer): string =>
  btoa(String.fromCharCode(...new Uint8Array(buf)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');

const base64urlFromString = (str: string): string =>
  btoa(encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (_, p1: string) => String.fromCharCode(parseInt(p1, 16))))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');

interface DpopKeyPair {
  privateKey: CryptoKey;
  publicKeyJwk: JsonWebKey & { kid: string };
}

interface StoredDpopKeyPair extends DpopKeyPair {
  id: string;
}

const DPOP_DB_NAME = 'pbb-dpop';
const DPOP_DB_VERSION = 1;
const DPOP_STORE_NAME = 'keys';
const DPOP_KEY_ID = 'current';

@Injectable({
  providedIn: 'root',
})
export class DpopService {
  private readonly keyPairPromise: Promise<DpopKeyPair>;

  constructor() {
    this.keyPairPromise = this.loadOrGenerateKeyPair();
  }

  private async loadOrGenerateKeyPair(): Promise<DpopKeyPair> {
    const storedKeyPair = await this.getStoredKeyPair();
    if (storedKeyPair) {
      return storedKeyPair;
    }

    this.clearTokenForMissingStoredKey();
    const generatedKeyPair = await this.generateKeyPair();
    await this.storeKeyPair(generatedKeyPair);
    return generatedKeyPair;
  }

  private clearTokenForMissingStoredKey(): void {
    if (getCookie(CNAME.PBB_UI_TOKEN)) {
      setCookie(CNAME.PBB_UI_TOKEN, null, -1);
    }
  }

  private async generateKeyPair(): Promise<DpopKeyPair> {
    const kp = (await crypto.subtle.generateKey(
      { name: 'ECDSA', namedCurve: 'P-256' },
      false,
      ['sign', 'verify']
    )) as CryptoKeyPair;

    const publicKeyJwk = await crypto.subtle.exportKey('jwk', kp.publicKey);
    const kid = uuidv4();

    return {
      privateKey: kp.privateKey,
      publicKeyJwk: { ...publicKeyJwk, kid } as JsonWebKey & { kid: string },
    };
  }

  private openDatabase(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      if (typeof indexedDB === 'undefined') {
        reject(new Error('IndexedDB is not available'));
        return;
      }

      const request = indexedDB.open(DPOP_DB_NAME, DPOP_DB_VERSION);

      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(DPOP_STORE_NAME)) {
          db.createObjectStore(DPOP_STORE_NAME, { keyPath: 'id' });
        }
      };

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error || new Error('Failed to open DPoP key database'));
    });
  }

  private async getStoredKeyPair(): Promise<DpopKeyPair | undefined> {
    let db: IDBDatabase | undefined;

    try {
      db = await this.openDatabase();
      const storedKeyPair = await new Promise<StoredDpopKeyPair | undefined>((resolve, reject) => {
        const transaction = db!.transaction(DPOP_STORE_NAME, 'readonly');
        const store = transaction.objectStore(DPOP_STORE_NAME);
        const request = store.get(DPOP_KEY_ID);

        request.onsuccess = () => resolve(request.result as StoredDpopKeyPair | undefined);
        request.onerror = () => reject(request.error || new Error('Failed to read DPoP key'));
      });

      if (storedKeyPair?.privateKey && storedKeyPair.publicKeyJwk?.kid) {
        return {
          privateKey: storedKeyPair.privateKey,
          publicKeyJwk: storedKeyPair.publicKeyJwk,
        };
      }
    } catch {
      return undefined;
    } finally {
      db?.close();
    }

    return undefined;
  }

  private async storeKeyPair(keyPair: DpopKeyPair): Promise<void> {
    let db: IDBDatabase | undefined;

    try {
      db = await this.openDatabase();
      await new Promise<void>((resolve, reject) => {
        const transaction = db!.transaction(DPOP_STORE_NAME, 'readwrite');
        const store = transaction.objectStore(DPOP_STORE_NAME);

        store.put({
          id: DPOP_KEY_ID,
          privateKey: keyPair.privateKey,
          publicKeyJwk: keyPair.publicKeyJwk,
        } satisfies StoredDpopKeyPair);

        transaction.oncomplete = () => resolve();
        transaction.onerror = () => reject(transaction.error || new Error('Failed to store DPoP key'));
        transaction.onabort = () => reject(transaction.error || new Error('DPoP key storage transaction aborted'));
      });
    } catch {
      // If IndexedDB is unavailable, keep using the in-memory non-extractable key for this page lifetime.
    } finally {
      db?.close();
    }
  }

  /**
   * Create a DPoP proof JWT.
   * @param htm  HTTP method (e.g. 'POST', 'GET')
   * @param htu  Full request URL — must match exactly
   * @param accessToken  Provide only for resource requests (adds `ath` claim).
   *                     Omit for the token endpoint.
   */
  async createProof(htm: string, htu: string, accessToken?: string): Promise<string> {
    const { privateKey, publicKeyJwk } = await this.keyPairPromise;

    const header = {
      typ: 'dpop+jwt',
      alg: 'ES256',
      jwk: {
        kty: publicKeyJwk.kty,
        use: 'sig',
        crv: publicKeyJwk.crv,
        kid: publicKeyJwk.kid,
        x: publicKeyJwk.x,
        y: publicKeyJwk.y,
        alg: 'ES256',
      },
    };

    const payload: Record<string, unknown> = {
      htm: htm.toUpperCase(),
      htu,
      iat: Math.floor(Date.now() / 1000),
      jti: uuidv4(),
    };

    if (accessToken) {
      payload['ath'] = await this.computeAth(accessToken);
    }

    const headerB64 = base64urlFromString(JSON.stringify(header));
    const payloadB64 = base64urlFromString(JSON.stringify(payload));
    const signingInput = `${headerB64}.${payloadB64}`;

    const sig = await crypto.subtle.sign(
      { name: 'ECDSA', hash: { name: 'SHA-256' } },
      privateKey,
      new TextEncoder().encode(signingInput)
    );

    return `${signingInput}.${base64urlFromBuffer(sig)}`;
  }

  private async computeAth(accessToken: string): Promise<string> {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(accessToken));
    return base64urlFromBuffer(digest);
  }
}
